'use client'

import React, { useEffect, useState } from 'react'
import PrimaryButton from '@/src/components/ui/primary-button'
import SecondaryButton from '@/src/components/ui/secondary-button'

const isBrowser = typeof window !== 'undefined'

function useHasMounted(): boolean {
  const [hasMounted, setHasMounted] = useState<boolean>(false)
  useEffect(() => {
    setHasMounted(true)
  }, [])
  return hasMounted
}

function useStickyState<T>(
  defaultValue: T,
  key: string
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    if (isBrowser) {
      const stickyValue = window.localStorage.getItem(key)
      return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue
    } else {
      return defaultValue
    }
  })

  useEffect(() => {
    if (isBrowser) {
      window.localStorage.setItem(key, JSON.stringify(value))
    }
  }, [key, value])

  return [value, setValue]
}

const CookieConsent: React.FC = () => {
  const hasMounted = useHasMounted()

  const [bannerHidden, setBannerHidden] = useStickyState<boolean>(false, 'consentCookieHidden')

  const EnableAnalytics = () => {
    document.cookie = 'gdpr-google-tagmanager=true; path=/; max-age=31536000' // 1 year expiry
    window.dispatchEvent(new Event('analytics-consent-granted'))
    setBannerHidden(true)
  }

  const DeclineAnalytics = () => {
    document.cookie = 'gdpr-google-tagmanager=; path=/; max-age=0'
    window.dispatchEvent(new Event('analytics-consent-denied'))
    setBannerHidden(true)
  }

  if (!hasMounted) {
    return null
  }

  return (
    <>
      {!bannerHidden && (
        <div className="fixed bottom-0 left-0 z-[999] w-full border border-white/10 bg-[#0b0f1a] px-4 py-4 shadow-[0_0_18px_rgba(80,102,255,0.16)] transition-colors sm:bottom-4 sm:left-4 sm:right-4 sm:flex sm:w-auto sm:items-center sm:gap-6 sm:rounded-xl sm:px-5 sm:py-3 lg:left-auto lg:w-[560px]">
          <span className="block text-sm leading-6 text-white/85 sm:flex-1">
            We use optional analytics cookies to understand site traffic. Read our{' '}
            <a href="/cookie-policy" target="_blank" className="text-link-blue underline underline-offset-2 hover:text-accent/80">
              cookie policy
            </a>.
          </span>
          <div className="mt-3 flex gap-2.5 sm:mt-0 sm:w-[280px] sm:flex-none">
            <PrimaryButton
              onClick={EnableAnalytics}
              fullWidth
              size="md"
            >
              Accept
            </PrimaryButton>
            <SecondaryButton
              onClick={DeclineAnalytics}
              fullWidth
              size="md"
            >
              Decline
            </SecondaryButton>
          </div>
        </div>
      )}
    </>
  )
}

export default CookieConsent
